import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Check, CheckCircle2, Clipboard, Link2, Mail, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SignupHeader } from "@/components/SignupHeader";
import { useAuth } from "@/hooks/useAuth";
import {
  SHIPPER_PARTNER_RESULTS_KEY,
  type PartnerInviteResult,
} from "@/lib/partnerInviteResults";

function readPartnerResults(): PartnerInviteResult[] {
  try {
    const value = sessionStorage.getItem(SHIPPER_PARTNER_RESULTS_KEY);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function SignupWelcome() {
  const [, setLocation] = useLocation();
  const { user, isLoading } = useAuth();
  const [copied, setCopied] = useState(false);
  const partnerResults = useMemo(readPartnerResults, []);
  const partnerLink = user?.referralCode
    ? `${window.location.origin}/join/${user.referralCode}`
    : "";

  useEffect(() => {
    if (!isLoading && (!user || user.userType !== "Shipper")) setLocation("/dashboard");
  }, [isLoading, setLocation, user]);

  const copyLink = async () => {
    if (!partnerLink) return;
    try {
      await navigator.clipboard.writeText(partnerLink);
    } catch {
      const input = document.querySelector<HTMLInputElement>("[data-testid='input-welcome-partner-link']");
      input?.select();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2200);
  };

  if (isLoading || !user || user.userType !== "Shipper") {
    return <div className="min-h-[100dvh] bg-background" aria-busy="true" />;
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      <SignupHeader showBack={false} />
      <main className="mx-auto flex w-full max-w-2xl justify-center px-4 pb-12 pt-28 sm:px-6">
        <div className="w-full">
          <header className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary ring-8 ring-primary/5">
              <Truck className="h-8 w-8" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">You're all set</h1>
            <p className="mt-2 text-muted-foreground">Your profile is complete and ready to go.</p>
          </header>

          <div className="space-y-4">
            {partnerResults.length > 0 && (
              <Card className="border-border/80 shadow-sm">
                <CardContent className="p-5 sm:p-6">
                  <h2 className="mb-4 text-lg font-semibold">Your freight partners</h2>
                  <div className="space-y-3">
                    {partnerResults.map((partner, index) => {
                      const added = partner.status === "added";
                      const invited = partner.status === "invited";
                      return (
                        <div
                          key={`${partner.email}-${index}`}
                          className={`flex items-start gap-3 rounded-lg border p-3.5 ${
                            added
                              ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/20"
                              : "border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/20"
                          }`}
                        >
                          {added ? (
                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
                          ) : (
                            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                          )}
                          <div className="min-w-0 text-sm">
                            {added ? (
                              <>
                                <p className="font-medium">{partner.name} · already on Fill The Trucks</p>
                                <p className="mt-0.5 text-emerald-700 dark:text-emerald-400">Added as your partner</p>
                              </>
                            ) : (
                              <>
                                <p className="font-medium">{partner.email}</p>
                                 <p className="mt-0.5 leading-relaxed text-amber-800 dark:text-amber-300">
                                   {invited
                                     ? "Invite email sent — but email sometimes lands in spam. Use your link below to be sure they connect."
                                     : "We couldn't send the invite email. Use your link below to be sure they connect."}
                                 </p>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border-primary/20 bg-primary/[0.03] shadow-sm">
              <CardContent className="p-5 sm:p-6">
                <div className="mb-1 flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-primary" aria-hidden="true" />
                  <h2 className="text-lg font-semibold">Your partner link</h2>
                </div>
                <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
                  The most reliable way to connect. Share it directly and they'll join as your partner right away.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    readOnly
                    value={partnerLink}
                    aria-label="Your partner link"
                    className="bg-background font-mono text-xs sm:text-sm"
                    data-testid="input-welcome-partner-link"
                  />
                  <Button type="button" variant="outline" onClick={copyLink} disabled={!partnerLink} className="shrink-0 sm:min-w-24">
                    {copied ? <Check className="mr-1.5 h-4 w-4 text-emerald-600" aria-hidden="true" /> : <Clipboard className="mr-1.5 h-4 w-4" aria-hidden="true" />}
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <p className="flex items-start gap-2 px-1 text-sm text-muted-foreground">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>Add hello@mail.fillthetrucks.com to your contacts — keep us rolling straight to your inbox.</span>
            </p>

            <Button
              className="mt-3 w-full"
              size="lg"
              onClick={() => {
                try {
                  sessionStorage.removeItem(SHIPPER_PARTNER_RESULTS_KEY);
                } catch {
                  // Storage can be unavailable in privacy-restricted browsers.
                }
                setLocation("/dashboard");
              }}
              data-testid="button-continue-dashboard"
            >
              Continue to dashboard
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}